<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import TalkviaForumAPI from 'dashboard/api/talkviaForum';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ForumComposerExtras from './ForumComposerExtras.vue';
import ForumCategoryBadge from './ForumCategoryBadge.vue';

const router = useRouter();
const { accountScopedRoute } = useAccount();
const loading = ref(true);
const saving = ref(false);
const composer = ref(null);
const preview = ref(false);
const extra = reactive({ steps: '', expected: '' });
const categoryIcons = {
  novidades: 'i-lucide-sparkles',
  'primeiros-passos': 'i-hugeicons-compass',
  atendimento: 'i-lucide-messages-square',
  'automacao-e-ia': 'i-lucide-bot',
  'canais-e-integracoes': 'i-lucide-plug',
  'ideias-e-sugestoes': 'i-lucide-lightbulb',
};
const topicFiles = ref([]);
const topicMentionedUsers = ref([]);
const overview = ref({ categories: [], membership: {}, stats: {} });
const topics = ref([]);
const meta = ref({ page: 1, total: 0, has_more: false });
const collapsedCategories = ref([]);
const groupedTopics = computed(() => {
  const groups = new Map();
  overview.value.categories.forEach(category => {
    groups.set(category.id, { category, topics: [] });
  });
  topics.value.forEach(topic => {
    if (!groups.has(topic.category.id)) {
      groups.set(topic.category.id, { category: topic.category, topics: [] });
    }
    groups.get(topic.category.id).topics.push(topic);
  });
  return [...groups.values()].filter(group => group.topics.length);
});
const toggleCategory = id => {
  collapsedCategories.value = collapsedCategories.value.includes(id)
    ? collapsedCategories.value.filter(value => value !== id)
    : [...collapsedCategories.value, id];
};

const filters = reactive({
  q: '',
  category_id: '',
  sort: 'activity',
  solved: '',
});
const form = reactive({ title: '', body: '', talkvia_forum_category_id: '' });

const composedBody = computed(() =>
  [
    form.body.trim(),
    extra.steps.trim()
      ? `O que já tentei / como reproduzir\n${extra.steps.trim()}`
      : '',
    extra.expected.trim()
      ? `Resultado esperado / próximos passos\n${extra.expected.trim()}`
      : '',
  ]
    .filter(Boolean)
    .join('\n\n')
);
const selectedCategory = computed(() =>
  overview.value.categories.find(
    category => category.id === form.talkvia_forum_category_id
  )
);
const openComposer = () => {
  if (filters.category_id) form.talkvia_forum_category_id = filters.category_id;
  preview.value = false;
  composer.value.open();
};
const canPublishTopic = computed(
  () =>
    form.title.trim().length >= 3 &&
    form.body.trim().length >= 2 &&
    composedBody.value.length <= 20000 &&
    form.title.trim().length <= 160 &&
    Boolean(form.talkvia_forum_category_id) &&
    !saving.value
);

const categoryOptions = computed(() => [
  { value: '', label: 'Todas as categorias' },
  ...overview.value.categories.map(category => ({
    value: category.id,
    label: category.name,
  })),
]);
const sortOptions = [
  { value: 'activity', label: 'Atividade recente' },
  { value: 'newest', label: 'Mais novos' },
  { value: 'popular', label: 'Mais visualizados' },
];
const solvedOptions = [
  { value: '', label: 'Todos os tópicos' },
  { value: 'true', label: 'Resolvidos' },
  { value: 'false', label: 'Sem solução' },
];

const loadTopics = async (page = 1, append = false) => {
  try {
    const params = { ...filters, page };
    Object.keys(params).forEach(
      key => params[key] === '' && delete params[key]
    );
    const { data } = await TalkviaForumAPI.topics(params);
    if (!append) collapsedCategories.value = [];
    topics.value = append ? [...topics.value, ...data.payload] : data.payload;
    meta.value = data.meta;
  } catch (error) {
    useAlert('Não foi possível carregar os tópicos da comunidade.');
  }
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await TalkviaForumAPI.overview();
    overview.value = data;
    form.talkvia_forum_category_id = data.categories[0]?.id || '';
    await loadTopics();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível abrir a comunidade.'
    );
  } finally {
    loading.value = false;
  }
};

const createTopic = async () => {
  if (saving.value) return;
  if (!canPublishTopic.value) {
    useAlert(
      'Confira título, categoria e detalhes. O conteúdo completo deve ter até 20.000 caracteres.'
    );
    return;
  }
  saving.value = true;
  try {
    const { data } = await TalkviaForumAPI.createTopic(
      { ...form, title: form.title.trim(), body: composedBody.value },
      topicFiles.value,
      topicMentionedUsers.value.map(user => user.id)
    );
    composer.value.close();
    router.push(
      accountScopedRoute('talkvia_forum_topic', { topicId: data.id })
    );
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível publicar o tópico.'
    );
  } finally {
    saving.value = false;
  }
};

const openTopic = topic =>
  router.push(accountScopedRoute('talkvia_forum_topic', { topicId: topic.id }));
const formatDate = value =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));

onMounted(load);
</script>

<template>
  <main
    class="h-full min-h-0 w-full min-w-0 space-y-5 overflow-y-auto p-4 sm:p-6"
  >
    <header
      class="forum-hero relative isolate overflow-hidden rounded-2xl p-6 shadow-lg sm:p-8"
    >
      <div class="forum-hero__glow" aria-hidden="true" />
      <div class="forum-hero__world" aria-hidden="true">
        <span class="forum-hero__orbit">
          <span class="forum-hero__satellite" />
        </span>
        <Icon icon="i-lucide-globe-2" class="forum-hero__globe" />
      </div>
      <div
        class="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between"
      >
        <div class="max-w-2xl">
          <p
            class="!text-xs !font-semibold uppercase tracking-[0.2em] !text-white/75"
          >
            Comunidade Talkvia
          </p>
          <h1 class="mt-2 !text-2xl !font-semibold !text-white sm:!text-3xl">
            Aprenda, compartilhe e evolua junto
          </h1>
          <p class="mt-2 !text-sm !leading-6 !text-white/85">
            Tire dúvidas, compartilhe boas práticas e encontre soluções
            construídas por quem usa a Talkvia todos os dias.
          </p>
        </div>
        <Button
          v-if="overview.membership.can_participate"
          label="Criar novo tópico"
          icon="i-lucide-plus"
          color="slate"
          class="forum-hero__button flex-shrink-0 !bg-white !text-slate-900 !outline-white/30 hover:!bg-slate-100 focus-visible:!bg-slate-100"
          @click="openComposer"
        />
      </div>
      <div
        class="relative z-10 mt-6 grid grid-cols-3 gap-3 border-t border-white/20 pt-5"
      >
        <div>
          <p class="!text-xl !font-semibold !text-white">
            {{ overview.stats.topics || 0 }}
          </p>
          <p class="!text-xs !text-white/70">Tópicos</p>
        </div>
        <div>
          <p class="!text-xl !font-semibold !text-white">
            {{ overview.stats.posts || 0 }}
          </p>
          <p class="!text-xs !text-white/70">Respostas</p>
        </div>
        <div>
          <p class="!text-xl !font-semibold !text-white">
            {{ overview.stats.members || 0 }}
          </p>
          <p class="!text-xs !text-white/70">Membros</p>
        </div>
      </div>
    </header>

    <div v-if="loading" class="flex min-h-64 items-center justify-center">
      <Icon icon="i-lucide-loader-2" class="size-7 animate-spin text-n-brand" />
    </div>
    <template v-else>
      <div
        v-if="!overview.membership.can_participate"
        class="rounded-xl border border-n-amber-6 bg-n-amber-2 p-4 text-sm text-n-amber-11"
      >
        <strong>Participação suspensa.</strong> Você ainda pode consultar o
        conteúdo, mas não pode publicar ou interagir.
      </div>
      <section
        aria-label="Categorias da comunidade"
        class="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6"
      >
        <button
          v-for="category in overview.categories"
          :key="category.id"
          type="button"
          class="min-w-0 rounded-xl border bg-n-solid-1 p-3 text-left transition hover:border-n-brand hover:bg-n-alpha-1"
          :class="
            filters.category_id === category.id
              ? 'border-n-brand ring-1 ring-n-brand/20'
              : 'border-n-weak'
          "
          :aria-pressed="filters.category_id === category.id"
          :title="category.description"
          @click="
            filters.category_id =
              filters.category_id === category.id ? '' : category.id;
            loadTopics();
          "
        >
          <div class="flex items-center justify-between gap-2">
            <Icon
              :icon="categoryIcons[category.slug] || 'i-lucide-hash'"
              class="size-5 shrink-0"
              :style="{ color: category.color }"
            />
            <span class="text-xs tabular-nums text-n-slate-11">{{
              category.topics_count
            }}</span>
          </div>
          <strong class="mt-2 block text-sm font-medium text-n-slate-12">{{
            category.name
          }}</strong>
          <span class="mt-1 line-clamp-2 text-xs leading-4 text-n-slate-11">{{
            category.description
          }}</span>
        </button>
      </section>

      <section
        class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm"
      >
        <div
          class="grid gap-3 border-b border-n-weak p-4 sm:grid-cols-2 2xl:grid-cols-[minmax(12rem,1fr)_12rem_10rem_10rem_auto]"
        >
          <Input
            v-model="filters.q"
            type="search"
            placeholder="Buscar dúvidas e soluções"
            @enter="loadTopics()"
          /><Select
            v-model="filters.category_id"
            :options="categoryOptions"
          /><Select v-model="filters.solved" :options="solvedOptions" /><Select
            v-model="filters.sort"
            :options="sortOptions"
          /><Button
            label="Buscar"
            icon="i-lucide-search"
            @click="loadTopics()"
          />
        </div>
        <div
          v-if="!topics.length"
          class="flex flex-col items-center gap-3 px-6 py-16 text-center"
        >
          <Icon
            icon="i-lucide-messages-square"
            class="size-12 text-n-slate-7"
          />
          <p class="text-sm text-n-slate-10">Nenhum tópico encontrado.</p>
        </div>
        <div v-else class="space-y-4 bg-n-alpha-1 p-3 sm:p-4">
          <section
            v-for="group in groupedTopics"
            :key="group.category.id"
            :aria-labelledby="`forum-category-title-${group.category.id}`"
            class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
          >
            <h2>
              <button
                type="button"
                class="forum-category-toggle flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand sm:px-5"
                :style="{
                  '--forum-category-color': /^#[0-9a-f]{6}$/i.test(
                    group.category.color
                  )
                    ? group.category.color
                    : '#64748B',
                }"
                :aria-expanded="
                  !collapsedCategories.includes(group.category.id)
                "
                :aria-controls="`forum-category-topics-${group.category.id}`"
                @click="toggleCategory(group.category.id)"
              >
                <span class="flex min-w-0 items-center gap-3">
                  <Icon
                    :icon="
                      categoryIcons[group.category.slug] || 'i-lucide-hash'
                    "
                    class="size-5 shrink-0 text-n-slate-11"
                  />
                  <span
                    :id="`forum-category-title-${group.category.id}`"
                    class="min-w-0"
                  >
                    <ForumCategoryBadge :category="group.category" />
                  </span>
                </span>
                <span
                  class="flex shrink-0 items-center gap-3 text-xs text-n-slate-11"
                >
                  <span :aria-label="`${group.topics.length} tópicos exibidos`">
                    {{ group.topics.length }}
                    <span class="hidden sm:inline">{{
                      group.topics.length === 1
                        ? 'tópico exibido'
                        : 'tópicos exibidos'
                    }}</span>
                  </span>
                  <Icon
                    icon="i-lucide-chevron-down"
                    class="size-4 transition-transform"
                    :class="{
                      '-rotate-90': collapsedCategories.includes(
                        group.category.id
                      ),
                    }"
                  />
                </span>
              </button>
            </h2>
            <div
              v-show="!collapsedCategories.includes(group.category.id)"
              :id="`forum-category-topics-${group.category.id}`"
              class="divide-y divide-n-weak"
            >
              <button
                v-for="topic in group.topics"
                :key="topic.id"
                type="button"
                class="grid w-full gap-4 p-4 text-left transition hover:bg-n-alpha-1 sm:grid-cols-[1fr_auto] sm:p-5"
                @click="openTopic(topic)"
              >
                <div class="min-w-0">
                  <div
                    v-if="topic.pinned || topic.solved"
                    class="mb-2 flex flex-wrap items-center gap-2"
                  >
                    <span
                      v-if="topic.pinned"
                      class="rounded-full bg-n-violet-3 px-2 py-0.5 text-xs font-medium text-n-violet-11"
                      >Fixado</span
                    ><span
                      v-if="topic.solved"
                      class="rounded-full bg-n-teal-3 px-2 py-0.5 text-xs font-medium text-n-teal-11"
                      >Resolvido</span
                    >
                  </div>
                  <h2 class="text-base font-semibold text-n-slate-12">
                    {{ topic.title }}
                  </h2>
                  <p
                    class="mt-1 line-clamp-2 text-sm leading-6 text-n-slate-10"
                  >
                    {{ topic.preview }}
                  </p>
                  <p class="mt-3 text-xs text-n-slate-9">
                    Por {{ topic.author.name }} · atividade
                    {{ formatDate(topic.last_activity_at) }}
                  </p>
                </div>
                <div
                  class="flex items-center gap-5 text-xs text-n-slate-9 sm:flex-col sm:items-end sm:justify-center sm:gap-2"
                >
                  <span class="inline-flex items-center gap-1"
                    ><Icon icon="i-lucide-message-circle" class="size-4" />{{
                      topic.posts_count
                    }}</span
                  ><span class="inline-flex items-center gap-1"
                    ><Icon icon="i-lucide-eye" class="size-4" />{{
                      topic.views_count
                    }}</span
                  >
                </div>
              </button>
            </div>
          </section>
        </div>
        <div
          v-if="meta.has_more"
          class="border-t border-n-weak p-4 text-center"
        >
          <Button
            label="Carregar mais"
            color="slate"
            variant="faded"
            @click="loadTopics(meta.page + 1, true)"
          />
        </div>
      </section>
    </template>

    <Dialog
      ref="composer"
      title="Criar novo tópico"
      description="Compartilhe uma dúvida, ideia ou novidade com a comunidade."
      width="4xl"
      overflow-y-auto
      sticky-header-footer
      confirm-button-label="Publicar tópico"
      :is-loading="saving"
      :disable-confirm-button="!canPublishTopic"
      :close-on-click-outside="!saving"
      @confirm="createTopic"
    >
      <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_15rem]">
        <div class="min-w-0 space-y-4">
          <Input
            v-model="form.title"
            label="Título do tópico"
            placeholder="Qual é o assunto que você quer compartilhar?"
            maxlength="160"
            :disabled="saving"
          />
          <div class="space-y-2">
            <span class="text-sm font-medium">Categoria</span>
            <Select
              v-model="form.talkvia_forum_category_id"
              :options="categoryOptions.slice(1)"
              placeholder="Selecione uma categoria"
              search-placeholder="Buscar categoria"
              aria-label="Categoria do tópico"
              teleport-to-body
              :disabled="saving"
            />
          </div>
          <div class="flex gap-2 border-b border-n-weak pb-3">
            <Button
              label="Escrever"
              icon="i-lucide-pencil"
              :variant="preview ? 'ghost' : 'faded'"
              color="slate"
              sm
              @click="preview = false"
            />
            <Button
              label="Prévia"
              icon="i-lucide-eye"
              :variant="preview ? 'faded' : 'ghost'"
              color="slate"
              sm
              @click="preview = true"
            />
          </div>
          <template v-if="!preview">
            <TextArea
              v-model="form.body"
              label="Conteúdo e contexto"
              placeholder="Conte o que aconteceu ou apresente sua ideia. Inclua os detalhes que ajudam outras pessoas a entender o assunto."
              min-height="12rem"
              :max-length="20000"
              show-character-count
              resize
              :disabled="saving"
            />
            <details class="rounded-lg border border-n-weak p-3">
              <summary
                class="cursor-pointer text-sm font-medium text-n-slate-12"
              >
                Complementar com passos e resultado (opcional)
              </summary>
              <div class="mt-4 space-y-4">
                <TextArea
                  v-model="extra.steps"
                  label="O que já tentei / como reproduzir"
                  placeholder="Descreva os passos, testes ou exemplos relevantes."
                  :max-length="6000"
                  min-height="6rem"
                  resize
                  :disabled="saving"
                />
                <TextArea
                  v-model="extra.expected"
                  label="Resultado esperado / próximos passos"
                  placeholder="Explique o que você gostaria de alcançar ou como aproveitar a novidade."
                  :max-length="6000"
                  min-height="6rem"
                  resize
                  :disabled="saving"
                />
              </div>
            </details>
          </template>
          <article
            v-else
            class="min-h-56 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
            data-testid="topic-preview"
          >
            <span class="text-xs text-n-brand">{{
              selectedCategory?.name
            }}</span>
            <h3 class="mt-2 break-words text-lg font-semibold">
              {{ form.title || 'Título do tópico' }}
            </h3>
            <p
              class="mt-4 whitespace-pre-wrap break-words text-sm leading-7 text-n-slate-11"
            >
              {{
                composedBody ||
                'O conteúdo aparecerá aqui enquanto você escreve.'
              }}
            </p>
          </article>
          <p
            class="text-xs"
            :class="
              composedBody.length > 20000 ? 'text-n-ruby-11' : 'text-n-slate-11'
            "
          >
            {{ composedBody.length.toLocaleString('pt-BR') }} / 20.000
            caracteres no conteúdo completo
          </p>
          <ForumComposerExtras
            v-model:files="topicFiles"
            v-model:mentioned-users="topicMentionedUsers"
            :disabled="saving"
          />
        </div>
        <aside class="space-y-4">
          <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
            <Icon icon="i-lucide-lightbulb" class="size-5 text-n-brand" />
            <h3 class="mt-3 text-sm font-semibold">
              Um tópico fácil de entender
            </h3>
            <ul
              class="mt-3 list-disc space-y-3 pl-4 text-xs leading-5 text-n-slate-11"
            >
              <li>
                Use um título específico e escolha a categoria mais adequada.
              </li>
              <li>
                Explique o contexto e inclua exemplos, passos ou resultados.
              </li>
              <li>
                Anexe imagens ou arquivos e mencione quem pode contribuir.
              </li>
              <li>Confira a prévia antes de publicar.</li>
            </ul>
          </div>
          <p class="text-xs leading-5 text-n-slate-11">
            O tópico ficará visível para a comunidade. Remova dados pessoais,
            senhas e informações de clientes dos textos e anexos.
          </p>
        </aside>
      </div>
    </Dialog>
  </main>
</template>

<style scoped>
.forum-category-toggle {
  background-color: color-mix(
    in srgb,
    var(--forum-category-color) 12%,
    transparent
  );
  border-bottom: 1px solid
    color-mix(in srgb, var(--forum-category-color) 25%, transparent);
}

.forum-category-toggle:hover {
  background-color: color-mix(
    in srgb,
    var(--forum-category-color) 20%,
    transparent
  );
}

.forum-hero {
  background: radial-gradient(
      circle at 78% 10%,
      rgb(255 255 255 / 12%),
      transparent 28%
    ),
    linear-gradient(135deg, #715bd2 0%, #56579d 52%, #31445e 100%);
}

.forum-hero__glow {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgb(255 255 255 / 4%) 1px, transparent 1px),
    linear-gradient(90deg, rgb(255 255 255 / 4%) 1px, transparent 1px);
  background-size: 32px 32px;
  mask-image: linear-gradient(to left, black, transparent 68%);
  pointer-events: none;
}

.forum-hero__world {
  position: absolute;
  top: 50%;
  right: clamp(2rem, 13vw, 14rem);
  width: clamp(13rem, 22vw, 21rem);
  aspect-ratio: 1;
  color: white;
  opacity: 0.16;
  pointer-events: none;
  transform: translateY(-50%);
}

.forum-hero__globe {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 0 28px rgb(191 219 254 / 35%));
  animation: forum-globe-drift 12s ease-in-out infinite alternate;
}

.forum-hero__orbit {
  position: absolute;
  z-index: 1;
  inset: -8%;
  border: 1px solid rgb(255 255 255 / 55%);
  border-radius: 9999px;
  transform: rotate(-18deg);
}

.forum-hero__satellite {
  position: absolute;
  top: 47%;
  left: -0.28rem;
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 9999px;
  background: white;
  box-shadow: 0 0 16px 4px rgb(191 219 254 / 70%);
  animation: forum-satellite-pulse 2.4s ease-in-out infinite;
}

.forum-hero__button {
  box-shadow: 0 10px 30px rgb(15 23 42 / 22%);
}

@keyframes forum-globe-drift {
  from {
    transform: translate3d(-5px, -3px, 0) rotate(-2deg);
  }
  to {
    transform: translate3d(6px, 4px, 0) rotate(3deg);
  }
}

@keyframes forum-satellite-pulse {
  50% {
    opacity: 0.55;
    transform: scale(0.72);
  }
}

@media (max-width: 1023px) {
  .forum-hero__world {
    right: -3rem;
    opacity: 0.1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .forum-hero__globe,
  .forum-hero__satellite {
    animation: none;
  }
}
</style>
